export type ArticleStatus = "draft" | "published" | "scheduled" | "archived";
export const ARTICLE_TITLE_MIN_LENGTH = 50;
export const ARTICLE_TITLE_IDEAL_LENGTH = 55;
export const ARTICLE_TITLE_MAX_LENGTH = 60;
export const SEO_DESCRIPTION_MIN_LENGTH = 120;
export const SEO_DESCRIPTION_MAX_LENGTH = 160;

export const DEFAULT_SITE_SEO_DESCRIPTION = "Les Boules décrypte l’actualité française et internationale avec des articles satiriques, un regard décalé et un humour volontairement sérieux.";

export interface Category {
  id: string;
  name: string;
  slug: string;
  order: number;
  enabled: boolean;
  aliases?: string[];
  archivedAt?: string;
}

export interface SiteConfig {
  name: string;
  url: string;
  language: string;
  tagline: string;
  seoDescription?: string;
  author: string;
  articlesPerPage: number;
  timezone: string;
  template: string;
  disabledThemes?: string[];
  analyticsEnabled?: boolean;
  analyticsProvider?: "supabase" | "none";
  showViewCounter?: boolean;
  supabaseUrl?: string;
  supabaseAnonKey?: string;
  socialSharingEnabled?: boolean;
  socialNetworks?: string[];
  googleAnalyticsEnabled?: boolean;
  googleAnalyticsId?: string;
  contentMode?: "local" | "supabase";
  supabaseWorkspaceId?: string;
}

export interface ArticleFrontMatter {
  editorialId: string;
  title: string;
  slug: string;
  date: string;
  updated: string;
  categoryId: string;
  author: string;
  status: ArticleStatus;
  featured: boolean;
  image?: string;
  image_alt?: string;
  tags: string[];
  satire: boolean;
  seo_title?: string;
  seo_description?: string;
  locale?: string;
  aliases?: string[];
}

export interface ArticleRecord {
  id: string;
  filePath: string;
  frontMatter: ArticleFrontMatter;
  body: string;
  excerpt: string;
  version?: number;
}

export interface FeaturedArticleSettings {
  configured: boolean;
  articleId?: string;
  startsAt?: string;
  endsAt?: string;
  updatedAt?: string;
}

export interface PageFrontMatter {
  id: string;
  title: string;
  slug: string;
  order?: number;
  seo_title?: string;
  seo_description?: string;
  aliases?: string[];
}

export interface PageRecord {
  id: string;
  filePath: string;
  frontMatter: PageFrontMatter;
  body: string;
  excerpt: string;
  version?: number;
}

export interface MediaAssetRecord {
  id: string;
  publicPath: string;
  fileName: string;
  sizeBytes: number;
  createdAt: string;
  previewUrl?: string;
  width?: number;
  height?: number;
  variantPublicPaths?: string[];
  optimized?: boolean;
  sourceAvailable?: boolean;
}

export interface MediaOrphan {
  kind: "missing_storage" | "missing_metadata";
  mediaAssetId?: string;
  storagePath: string;
  publicPath: string;
}

export interface BuildReport {
  generatedAt: string;
  articlesBuilt: number;
  pagesBuilt: number;
  categoriesBuilt: number;
  searchPagesIndexed: number;
  assetsCopied: number;
  warnings: string[];
}

export type PublishProtocol = "sftp" | "ftps" | "ftp";
export type PublishMode = "passive" | "active";

export interface PublishProfile {
  id: string;
  name: string;
  protocol: PublishProtocol;
  host: string;
  port: number;
  username: string;
  remotePath: string;
  publicUrl: string;
  timeout: number;
  passiveMode?: PublishMode;
  sshFingerprint?: string;
  sshKeyPath?: string;
}

export interface PublishConfig {
  profiles: PublishProfile[];
  htaccessEnabled?: boolean;
  htaccessContent?: string;
}

export interface AutomaticPublishSettings {
  enabled: boolean;
  profileId: string;
  secretConfigured: boolean;
  secretUpdatedAt?: string;
  workerActive: boolean;
  workerLastSeenAt?: string;
}

export type ScheduledPublishJobStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";

export interface ScheduledPublishJob {
  id: string;
  profileId: string;
  triggerArticleId?: string;
  scheduledFor: string;
  nextAttemptAt: string;
  status: ScheduledPublishJobStatus;
  attempts: number;
  maxAttempts: number;
  leaseExpiresAt?: string;
  startedAt?: string;
  finishedAt?: string;
  report?: Record<string, unknown>;
  error?: string;
  createdAt: string;
}

export type PreflightSeverity = "info" | "warning" | "error";

export interface PreflightIssue {
  id: string;
  severity: PreflightSeverity;
  scope: "article" | "page" | "site" | "media" | "publishing" | "generated";
  message: string;
  resourceId?: string;
  resourceLabel?: string;
  action?: "open-article" | "open-page" | "open-media" | "open-publishing" | "open-google";
}

export interface PreflightReport {
  generatedAt: string;
  passedChecks: number;
  issues: PreflightIssue[];
  errors: number;
  warnings: number;
  infos: number;
  canPublish: boolean;
}

export interface PublicationManifestFile {
  path: string;
  sha256: string;
  sizeBytes: number;
}

export interface PublicationManifest {
  version: 1;
  profileId: string;
  generatedAt: string;
  files: PublicationManifestFile[];
}

export interface PublicationPlan {
  profileId: string;
  generatedAt: string;
  upload: PublicationManifestFile[];
  unchanged: PublicationManifestFile[];
  remove: PublicationManifestFile[];
  manifest: PublicationManifest;
}

export interface PublicationReport {
  id: string;
  profileId: string;
  publishedAt: string;
  protocol: string;
  host: string;
  remotePath: string;
  uploadedFiles: number;
  removedFiles: number;
  unchangedFiles: number;
  snapshotPath: string;
  restoredFrom?: string;
  preflight?: PreflightReport;
}
